/**
 * Inky's in-flight turns, shared by every surface (dock panel, mobile drawer, any future
 * full view). Ported from Flint's turn registry (cairn components/copilot/flint-turns.ts).
 *
 * WHY: the chat panel used to own the whole turn in component state, so switching to the
 * History tab, collapsing the dock or closing the mobile drawer unmounted the panel and
 * dropped the streaming reply, the tool progress and the AbortController while the loop
 * kept calling setState on an unmounted component. Every turn is now registered here
 * under a key, with the conversation it runs in (once the user message is saved), its
 * live progress (streamed text, steps), and the outcome. Surfaces read it through
 * `useSyncExternalStore`, so a remounted panel picks the turn back up mid-stream, and the
 * composer guard (`runningTurnFor`) is conversation-scoped instead of a per-component flag.
 *
 * A turn runs on the server (inky-agent, streamed over SSE) or, when the server agent is
 * unreachable, in the browser (agentLoop.js). Stop and Esc call `stopTurn`, which fires the
 * turn's AbortController. A server turn whose stream DROPS after it started is DETACHED, not
 * failed (Flint's detach-not-abort): the server keeps running it, the turn keeps holding the
 * composer guard, and the thread is polled until the reply lands.
 */

import { useMemo, useSyncExternalStore } from "react";

/** Finished turns are kept this long for a surface that remounts late, then collected. */
const KEEP_FINISHED_MS = 5 * 60_000;

/** @typedef {{id: string, tool: string, label: string, detail?: string, status: 'running'|'done'|'error', startedAt: number, ms?: number, summary?: string}} TurnStep */

/**
 * @typedef {object} AssistantTurn
 * @property {string} key - Turn key.
 * @property {string} origin - Surface that started it (e.g. 'dock').
 * @property {string|null} conversationId - Conversation, null until the user message is saved.
 * @property {{content: string}} userMessage - The message this turn answers.
 * @property {string|null} [userMessageId] - Saved row id of that message, once persisted.
 * @property {number} startedAt - Epoch ms when the turn began.
 * @property {'running'|'detached'|'done'|'failed'|'stopped'} status - Lifecycle state (`detached`: the
 *   stream dropped while the server kept working; still counts as running for the composer guard).
 * @property {{text: string, steps: TurnStep[], phase: string}} live - Live progress.
 * @property {{messageId?: string, error?: string, retryable?: boolean, retry?: object}|null} result - Outcome (`retry` carries what a Retry needs).
 * @property {number} [durationMs] - Wall time, set when the turn ends.
 * @property {boolean} [navigated] - The turn opened another page (app.navigate).
 */

const EMPTY_LIVE = Object.freeze({ text: "", steps: [], phase: "thinking" });

/** @type {Map<string, AssistantTurn>} */
let turns = new Map();
/** @type {Map<string, AbortController>} */
const controllers = new Map();
/** @type {Map<string, string>} Latest turn key per origin surface. */
const latestByOrigin = new Map();
let version = 0;
const listeners = new Set();

/**
 * Whether a turn is still in flight (running here, or detached and awaited from the thread).
 * @param {AssistantTurn|null|undefined} turn - Turn.
 * @returns {boolean} True while in flight.
 */
export const isTurnActive = (turn) => turn?.status === "running" || turn?.status === "detached";

/**
 * Drop finished turns older than {@link KEEP_FINISHED_MS}.
 * @param {number} now - Current epoch ms.
 * @returns {void}
 */
const collect = (now) => {
  for (const [key, turn] of turns) {
    if (isTurnActive(turn)) continue;
    const endedAt = turn.startedAt + (turn.durationMs || 0);
    if (now - endedAt > KEEP_FINISHED_MS) {
      turns.delete(key);
      controllers.delete(key);
      for (const [origin, latest] of latestByOrigin) {
        if (latest === key) latestByOrigin.delete(origin);
      }
    }
  }
};

/**
 * Notify subscribers of a registry change.
 * @returns {void}
 */
const emit = () => {
  collect(Date.now());
  version += 1;
  for (const listener of listeners) listener();
};

/**
 * Subscribe to registry changes.
 * @param {() => void} listener - Change listener.
 * @returns {() => void} Unsubscribe function.
 */
export const subscribeTurns = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/**
 * Current registry version (changes on every update).
 * @returns {number} Version counter.
 */
const getVersion = () => version;

let keySeq = 0;

/**
 * Random hex from the platform CSPRNG.
 * @param {number} bytes - Number of random bytes.
 * @returns {string} Hex string, or "" when no CSPRNG is available.
 */
const randomHex = (bytes) => {
  try {
    const cryptoApi = typeof window !== "undefined" ? window.crypto : undefined;
    if (cryptoApi?.randomUUID) return cryptoApi.randomUUID().replace(/-/g, "").slice(0, bytes * 2);
    if (cryptoApi?.getRandomValues) {
      return Array.from(cryptoApi.getRandomValues(new Uint8Array(bytes)), (b) => b.toString(16).padStart(2, "0")).join("");
    }
  } catch {
    /* fall through */
  }
  return "";
};

/**
 * Mint a fresh turn key. The server agent also uses it as the turn id a Stop request addresses,
 * so it must not be guessable: it carries 128 random bits (the sequence keeps keys unique even
 * on a platform without a CSPRNG).
 * @returns {string} Turn key (at most 80 characters of [A-Za-z0-9_-], as the server requires).
 */
export const newTurnKey = () => {
  keySeq += 1;
  const random = randomHex(16) || `${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
  return `turn-${Date.now().toString(36)}-${keySeq}-${random}`;
};

/**
 * Replace one turn through a pure update function.
 * @param {string} key - Turn key.
 * @param {(turn: AssistantTurn) => AssistantTurn} fn - Update function.
 * @returns {void}
 */
const patch = (key, fn) => {
  const turn = turns.get(key);
  if (!turn) return;
  const next = fn(turn);
  if (next === turn) return;
  turns.set(key, next);
  emit();
};

/**
 * Register a turn the moment it starts.
 * @param {{key: string, origin?: string, conversationId?: string|null, userMessage: {content: string}, userMessageId?: string|null}} input - Turn seed.
 * @returns {AbortController} Controller that stops the turn.
 */
export const beginTurn = ({ key, origin = "dock", conversationId = null, userMessage, userMessageId = null }) => {
  const controller = new AbortController();
  controllers.set(key, controller);
  turns.set(key, {
    key,
    origin,
    conversationId,
    userMessage,
    userMessageId,
    startedAt: Date.now(),
    status: "running",
    live: EMPTY_LIVE,
    result: null,
  });
  latestByOrigin.set(origin, key);
  emit();
  return controller;
};

/**
 * Fold one progress event into a running turn. Unknown events change nothing.
 *
 * Events: `conversation {conversationId, userMessageId}`, `text_delta {delta}`, `text_reset`,
 * `phase {phase}`, `step_start {step}`, `step_update {id, label}`, `step_end {id, status, summary}`,
 * `navigated` (the turn moved the user to another page; its conversation follows them).
 * @param {string} key - Turn key.
 * @param {{type: string}} event - Progress event.
 * @returns {void}
 */
export const applyTurnEvent = (key, event) => {
  patch(key, (turn) => {
    if (turn.status !== "running" || !event) return turn;
    const live = turn.live;
    switch (event.type) {
      case "conversation":
        return {
          ...turn,
          conversationId: event.conversationId || turn.conversationId,
          userMessageId: event.userMessageId || turn.userMessageId || null,
        };
      case "navigated":
        return turn.navigated ? turn : { ...turn, navigated: true };
      case "text_delta":
        return event.delta ? { ...turn, live: { ...live, text: live.text + event.delta, phase: "writing" } } : turn;
      case "text_reset":
        return live.text ? { ...turn, live: { ...live, text: "" } } : turn;
      case "phase":
        return event.phase && event.phase !== live.phase ? { ...turn, live: { ...live, phase: event.phase } } : turn;
      case "step_start":
        return {
          ...turn,
          live: { ...live, phase: "working", steps: [...live.steps, { status: "running", startedAt: Date.now(), ...event.step }] },
        };
      case "step_update":
        return {
          ...turn,
          live: {
            ...live,
            steps: live.steps.map((step) => (step.id === event.id && event.label ? { ...step, label: event.label } : step)),
          },
        };
      case "step_end":
        return {
          ...turn,
          live: {
            ...live,
            steps: live.steps.map((step) =>
              step.id === event.id
                ? {
                    ...step,
                    status: event.status || "done",
                    summary: event.summary ?? step.summary,
                    ms: Date.now() - step.startedAt,
                  }
                : step
            ),
          },
        };
      default:
        return turn;
    }
  });
};

/**
 * Settle a turn.
 * @param {string} key - Turn key.
 * @param {'done'|'failed'|'stopped'} status - Final status.
 * @param {object} [result] - Outcome details.
 * @returns {void}
 */
const settle = (key, status, result = {}) => {
  controllers.delete(key);
  patch(key, (turn) =>
    !isTurnActive(turn)
      ? turn
      : { ...turn, status, result, durationMs: Date.now() - turn.startedAt }
  );
};

/**
 * Detach a running turn whose stream dropped after the server took it: it stays in flight
 * (composer guard, working view) while the thread is polled for the reply. Its controller is
 * kept so Stop asks the server to stop.
 * @param {string} key - Turn key.
 * @returns {void}
 */
export const detachTurn = (key) =>
  patch(key, (turn) =>
    turn.status !== "running"
      ? turn
      : { ...turn, status: "detached", detachedAt: Date.now(), live: { ...turn.live, phase: turn.stopping ? "stopping" : "waiting" } }
  );

/**
 * Mark an in-flight server turn as stopping: Stop was sent and the server is ending its current
 * step. The turn keeps the composer guard until the server's stopped reply lands (the server
 * holds the conversation until then, so a new message would only be refused).
 * @param {string} key - Turn key.
 * @returns {void}
 */
export const markTurnStopping = (key) =>
  patch(key, (turn) =>
    !isTurnActive(turn) || turn.stopping ? turn : { ...turn, stopping: true, live: { ...turn.live, phase: "stopping" } }
  );

/**
 * Mark a turn finished with its saved reply.
 * @param {string} key - Turn key.
 * @param {{messageId?: string}} [result] - Outcome.
 * @returns {void}
 */
export const finishTurn = (key, result = {}) => settle(key, "done", result);

/**
 * Mark a turn failed.
 * @param {string} key - Turn key.
 * @param {string} error - Human-readable error.
 * @param {{retryable?: boolean, retry?: object}} [options] - Failure options (`retry` carries what a Retry needs).
 * @returns {void}
 */
export const failTurn = (key, error, { retryable = true, ...extra } = {}) =>
  settle(key, "failed", { error, retryable, ...extra });

/**
 * Mark a turn stopped by the user (after its controller fired).
 * @param {string} key - Turn key.
 * @param {object} [extra] - Outcome details (e.g. `messageId` of a saved partial reply, `retry`).
 * @returns {void}
 */
export const markTurnStopped = (key, extra = {}) => settle(key, "stopped", { retryable: true, ...extra });

/**
 * Stop a running turn: fires its AbortController. The loop settles it as stopped.
 * @param {string} key - Turn key.
 * @returns {boolean} True when a running turn was signalled.
 */
export const stopTurn = (key) => {
  const controller = controllers.get(key);
  if (!controller) return false;
  controller.abort();
  return true;
};

/**
 * Forget a turn (its outcome has been shown or is no longer relevant).
 * @param {string} key - Turn key.
 * @returns {void}
 */
export const dropTurn = (key) => {
  controllers.get(key)?.abort();
  controllers.delete(key);
  if (turns.delete(key)) {
    for (const [origin, latest] of latestByOrigin) {
      if (latest === key) latestByOrigin.delete(origin);
    }
    emit();
  }
};

/**
 * Read a turn by key.
 * @param {string|null|undefined} key - Turn key.
 * @returns {AssistantTurn|null} Turn.
 */
export const getTurn = (key) => (key ? turns.get(key) || null : null);

/**
 * The running turn in a conversation, whichever surface started it.
 * @param {string|null|undefined} conversationId - Conversation id.
 * @returns {AssistantTurn|null} Running turn.
 */
export const runningTurnFor = (conversationId) => {
  if (!conversationId) return null;
  for (const turn of turns.values()) {
    if (isTurnActive(turn) && turn.conversationId === conversationId) return turn;
  }
  return null;
};

/**
 * The running turn that owns the conversation on screen, so a route-driven conversation
 * switch must wait for it: a turn in `conversationId`, or (when no conversation is current)
 * a turn whose user message has not created its conversation yet.
 * @param {string|null} conversationId - Current conversation id, or null.
 * @returns {AssistantTurn|null} Blocking turn.
 */
export const runningTurnBlockingSwitch = (conversationId) => {
  const wanted = conversationId || null;
  for (const turn of turns.values()) {
    if (isTurnActive(turn) && (turn.conversationId || null) === wanted) return turn;
  }
  return null;
};

/**
 * Any running turn (used to guard a composer whose conversation does not exist yet).
 * @returns {AssistantTurn|null} A running turn.
 */
export const anyRunningTurn = () => {
  for (const turn of turns.values()) {
    if (isTurnActive(turn)) return turn;
  }
  return null;
};

/**
 * The latest turn started from a surface.
 * @param {string} origin - Surface name.
 * @returns {AssistantTurn|null} Turn.
 */
export const latestTurnFor = (origin) => getTurn(latestByOrigin.get(origin));

/**
 * Subscribe a component to the registry.
 * @returns {number} Registry version (forces a re-render on change).
 */
const useTurnsVersion = () => useSyncExternalStore(subscribeTurns, getVersion, getVersion);

/**
 * One turn by key, live.
 * @param {string|null|undefined} key - Turn key.
 * @returns {AssistantTurn|null} Turn.
 */
export const useAssistantTurn = (key) => {
  useTurnsVersion();
  return getTurn(key);
};

/**
 * The latest turn a surface started, live.
 * @param {string} origin - Surface name.
 * @returns {AssistantTurn|null} Turn.
 */
export const useLatestTurn = (origin) => {
  useTurnsVersion();
  return latestTurnFor(origin);
};

/**
 * The running turn in a conversation, live (shared composer guard).
 * @param {string|null|undefined} conversationId - Conversation id.
 * @returns {AssistantTurn|null} Running turn.
 */
export const useRunningTurn = (conversationId) => {
  useTurnsVersion();
  return runningTurnFor(conversationId);
};

/**
 * Conversation ids with a turn in flight, as one stable string ("id1|id2", sorted). It only
 * changes when that set changes, so a subscriber does not re-render on every streamed token.
 * @returns {string} Joined ids.
 */
const activeConversationKey = () => {
  const ids = new Set();
  for (const turn of turns.values()) {
    if (isTurnActive(turn) && turn.conversationId) ids.add(turn.conversationId);
  }
  return [...ids].sort().join("|");
};

/**
 * The conversations Inky is working in right now, live. Re-renders only when that set changes
 * (not on every text delta), for lists such as the History sidebar.
 * @returns {Set<string>} Conversation ids with a running or detached turn.
 */
export const useActiveConversationIds = () => {
  const key = useSyncExternalStore(subscribeTurns, activeConversationKey, activeConversationKey);
  return useMemo(() => new Set(key ? key.split("|") : []), [key]);
};

/**
 * Drop every turn (sign-out or account switch must not carry a turn into the next tenant).
 * @returns {void}
 */
export const resetTurns = () => {
  for (const controller of controllers.values()) controller.abort();
  controllers.clear();
  latestByOrigin.clear();
  turns = new Map();
  emit();
};
